import { useEffect, useRef, useState } from 'react';
import { logger } from '@zep/core-logger';
import { ErrorContent } from '@/components/ErrorBoundaryFallback';
import { DelayedLoadingFallback } from '@/shared/lms';
import { type LearningMaterial } from '@/views/build/modal/LearningMaterialUploadModal';
const SIZE_MB = 1024 ** 2;

// gview 가 간헐적으로 200 + application/binary(nosniff)를 주면 iframe 내비게이션이 `gview` 파일
// 다운로드로 바뀐다. allow-downloads 를 빼 다운로드를 막고 실패를 retry 에 넘긴다.
// allow-same-origin 이 빠지면 about:blank 가 opaque origin 이 되어 retry 판정(contentDocument)이 죽는다.
const VIEWER_IFRAME_SANDBOX = 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox';
export const FileViewer = ({
  file,
  onLoad
}: {
  file: Pick<LearningMaterial, 'url' | 'fileSize'>;
  onLoad?: () => void;
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(file.url)}&embedded=true`;
  const [isLoading, setIsLoading] = useState(true);
  const [isMaxLoadTimeReached, setIsMaxLoadTimeReached] = useState(false);
  const isTimeout = isLoading && isMaxLoadTimeReached;
  const loadRetryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clearLoadRetryIntervalRef = () => {
    if (loadRetryIntervalRef.current) {
      clearInterval(loadRetryIntervalRef.current);
    }
  };

  // 파일이 10MB 미만일 때와 이상일 때로 나누어 시간을 조절합니다.
  const iframeLoadRetryIntervalMS = (file.fileSize ?? 0) < 10 * SIZE_MB ? 2500 : 4000;
  const iframeMaxLoadTimeMS = (file.fileSize ?? 0) < 10 * SIZE_MB ? 15000 : 20000;

  // 로깅 용도
  const mountTimeRef = useRef(Date.now());
  useEffect(() => {
    // 구글 자체 에러로 인해 간헐적으로 iframe 로드를 실패하는 경우가 있어, iframeLoadRetryIntervalMS 간격으로 retry 합니다.
    loadRetryIntervalRef.current = setInterval(() => {
      // iframe 로드 실패 시
      if (iframeRef.current?.contentDocument?.URL === 'about:blank') {
        iframeRef.current.src = viewerUrl;
        logger.track('material_file_load_retry', {
          fileSizeMB: Math.round((file.fileSize ?? 0) / SIZE_MB)
        });
      }
    }, iframeLoadRetryIntervalMS);
    return () => clearLoadRetryIntervalRef();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const maxLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearMaxLoadTimerRef = () => {
    if (maxLoadTimerRef.current) {
      clearTimeout(maxLoadTimerRef.current);
    }
  };
  useEffect(() => {
    maxLoadTimerRef.current = setTimeout(() => {
      clearLoadRetryIntervalRef();
      setIsMaxLoadTimeReached(true);
      logger.track('material_file_load_timeout', {
        fileSizeMB: Math.round((file.fileSize ?? 0) / SIZE_MB)
      });
    }, iframeMaxLoadTimeMS);
    return () => clearMaxLoadTimerRef();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <div className="relative size-full bg-neutral" data-sentry-component="FileViewer" data-sentry-source-file="FileViewer.tsx">
      {/* iframeMaxLoadTimeMS에 도달하면 iframe 네트워크 요청을 끊습니다 */}
      {isTimeout ? <div className="flex size-full flex-col items-center justify-center gap-md">
          <ErrorContent />
        </div> : <>
          <DelayedLoadingFallback delayMS={500} className="absolute size-full" />
          <iframe ref={iframeRef} src={viewerUrl} sandbox={VIEWER_IFRAME_SANDBOX} className="z-2 absolute size-full" onLoad={() => {
        clearLoadRetryIntervalRef();
        clearMaxLoadTimerRef();
        setIsLoading(false);
        onLoad?.();
        logger.track('material_file_load_success', {
          fileSizeMB: Math.round((file.fileSize ?? 0) / SIZE_MB),
          spentSeconds: (Date.now() - mountTimeRef.current) / 1000
        });
      }} />
        </>}
    </div>;
};